import React, { Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loginActions, loginSelectors } from '../../../redux/login';
import { useLocation } from 'react-router';
import { push } from 'redux-first-history';
import { useTranslation } from 'react-i18next';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import classNames from 'classnames';
import Icon from '../../../components/envago/Icon/Icon';
import LanguageButton from '../../common/LanguageButton/LanguageButton';
import { helpSelectors } from '../../../redux/help';
import { applicationSelectors } from '../../../redux/application';
import { jobsSelectors } from '../../../redux/jobs';
import CommunicationHeaderSection from '../../common/Communication/CommunicationHeaderSection';
import HighContrastButton from '../../common/HighContrastButton/HighContrastButton';
import { useIsCompactLayout } from '../../../helper/LayoutHelper';
import { useUnsavedChanges } from '../../common/UnsavedChangesGuard/UnsavedChangesGuard';

interface Props {
    showIfNotLoggedIn?: boolean;
}

const MENU_ITEM_CLASSES = {
    base: 'px-3 py-2 rounded-md font-medium cursor-pointer whitespace-nowrap',
    mobileText: 'text-base w-full text-center block',
    desktopText: 'text-sm',
    active: 'bg-menu-item-bg-selected text-menu-item-text-selected',
    inactive: 'bg-menu-item-bg text-menu-item-text',
    hover: 'hover:bg-menu-item-bg-hover hover:text-menu-item-text-hover',
    activeHover: 'hover:bg-menu-item-bg-selected-hover hover:text-menu-item-text-selected-hover',
    focusInactive: 'focus:outline-none focus-visible:ring-2 focus-visible:ring-menu-item-ring focus:bg-menu-item-bg-focus focus:text-menu-item-text-focus',
    focusActive:
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-menu-item-ring-selected focus:bg-menu-item-bg-selected-focus focus:text-menu-item-text-selected-focus',
    pressedInactive: 'active:bg-menu-item-bg-selected active:text-menu-item-text-selected',
    pressedActive: 'active:bg-menu-item-bg-selected-hover active:text-menu-item-text-selected-hover',
};

export const getMenuClasses = (current: boolean, isMobileItem: boolean) =>
    classNames(
        MENU_ITEM_CLASSES.base,
        isMobileItem ? MENU_ITEM_CLASSES.mobileText : MENU_ITEM_CLASSES.desktopText,
        current ? MENU_ITEM_CLASSES.active : MENU_ITEM_CLASSES.inactive,
        current ? MENU_ITEM_CLASSES.activeHover : MENU_ITEM_CLASSES.hover,
        current ? MENU_ITEM_CLASSES.focusActive : MENU_ITEM_CLASSES.focusInactive,
        current ? MENU_ITEM_CLASSES.pressedActive : MENU_ITEM_CLASSES.pressedInactive,
    );

const NavigationBar = ({ showIfNotLoggedIn = false }: Props) => {
    const { t } = useTranslation();

    const isCompactLayout = useIsCompactLayout();

    const location = useLocation();
    const dispatch = useDispatch();
    const { confirmIfBlocked } = useUnsavedChanges();

    const isLoggedIn = useSelector(loginSelectors.isLoggedIn);
    const isEvaluationEnabled = useSelector(applicationSelectors.isEvaluationEnabled);
    const isGlobalHelpEnabled = useSelector(helpSelectors.isGlobalHelpEnabled);
    const reshareAllowed = useSelector(jobsSelectors.isAnyJobReshareAllowed);
    const hasSharableJobs = useSelector(jobsSelectors.hasShareableJobs);

    const navigation = showIfNotLoggedIn
        ? []
        : [
              {
                  name: t('menu.home'),
                  href: '/',
                  current: location.pathname === '/',
              },
          ];

    if (isLoggedIn) {
        navigation.push({
            name: t('menu.reading'),
            href: '/ablesung',
            current: location.pathname.indexOf('/ablesung') === 0,
        });
    }

    if (isLoggedIn && isEvaluationEnabled) {
        navigation.push({
            name: t('menu.evaluation'),
            href: '/auswertung',
            current: location.pathname.indexOf('/auswertung') === 0,
        });
    }

    if (isLoggedIn && reshareAllowed && hasSharableJobs) {
        navigation.push({
            name: t('menu.management'),
            href: '/management',
            current: location.pathname.indexOf('/management') === 0,
        });
    }

    if (isLoggedIn && isGlobalHelpEnabled) {
        navigation.push({
            name: t('menu.help'),
            href: '/help',
            current: location.pathname.indexOf('/help') === 0,
        });
    }

    if (!showIfNotLoggedIn) {
        if (!isLoggedIn) return null;
    }

    return (
        <Disclosure
            as={'nav'}
            className={'bg-header md:shadow-none shadow-lg text-headertext sticky w-full z-40 h-16 top-0'}
            aria-label={t('accessibility.navigation.header')}
        >
            {({ open: isMobileMenuOpen, close: closeMobileMenu }) => (
                <>
                    <div className="container mx-auto px-2 sm:px-2">
                        <div className="relative flex items-center justify-between h-16">
                            <div className="absolute inset-y-0 left-0 flex items-center md:hidden">
                                {/* Mobile menu button*/}
                                {navigation.length > 0 && (
                                    <Disclosure.Button
                                        aria-label={
                                            isMobileMenuOpen
                                                ? t('accessibility.navigation.closeMobileMenuButton')
                                                : t('accessibility.navigation.openMobileMenuButton')
                                        }
                                        aria-expanded={isMobileMenuOpen}
                                        id={'mobile-menu-button'}
                                        aria-controls={'mobile-menu'}
                                        aria-haspopup={'true'}
                                        className="mobile-menu-button inline-flex items-center justify-center p-2 rounded-md text-menu-item-bg bg-menu-item-text focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                                    >
                                        {isMobileMenuOpen ? (
                                            <Icon className={'block h-6 w-6'} aria={{ 'aria-hidden': 'true' }} path={Icon.Path.mdiClose} />
                                        ) : (
                                            <Icon path={Icon.Path.mdiMenu} className="block h-6 w-6" aria={{ 'aria-hidden': 'true' }} />
                                        )}
                                    </Disclosure.Button>
                                )}
                            </div>
                            <div className="flex-1 flex items-center justify-center md:items-stretch md:justify-start">
                                <div className="flex-shrink-0 flex items-center menu-logos">
                                    <img
                                        style={{
                                            imageRendering: 'auto',
                                        }}
                                        className="block lg:hidden h-8 w-auto menu-logo-mobile"
                                        src={'/api/public/application/logo.png'}
                                        alt="logo"
                                    />
                                    <img
                                        style={{
                                            imageRendering: 'auto',
                                        }}
                                        className="hidden lg:block h-8 w-auto menu-logo-desktop"
                                        src={'/api/public/application/logo.png'}
                                        alt="logo"
                                    />
                                </div>
                                <div className="hidden md:flex md:ml-6 flex-1 items-center">
                                    <div className="flex space-x-2 lg:space-x-4" role={'menu'} aria-label={t('accessibility.navigation.mainMenu')}>
                                        {navigation.map((item) => (
                                            <a
                                                role={'menuitem'}
                                                key={item.name}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    dispatch(push(item.href));
                                                }}
                                                href={item.href}
                                                className={getMenuClasses(item.current, false)}
                                                aria-current={item.current ? 'page' : undefined}
                                            >
                                                {item.name}
                                                {item.current && <span className="sr-only"> ({t('accessibility.navigation.current')})</span>}
                                            </a>
                                        ))}
                                    </div>
                                    <div className={'text-white flex-1'} />
                                </div>
                            </div>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-2 md:static md:inset-auto md:ml-6 md:pr-0 md:flex-shrink-0">
                                {isLoggedIn && (
                                    <Menu as="div" className="ml-3 relative" role={'menu'} aria-label={t('accessibility.navigation.moreMenu')}>
                                        {({ open: isUserMenuOpen, close: closeUserMenu }) => (
                                            <>
                                                <div className={'icon-buttons flex space-x-4'}>
                                                    <CommunicationHeaderSection />
                                                    {!isCompactLayout && (
                                                        <>
                                                            <HighContrastButton />
                                                            <LanguageButton />
                                                        </>
                                                    )}
                                                    <Menu.Button
                                                        role={'menuitem'}
                                                        aria-expanded={isUserMenuOpen}
                                                        aria-controls={'user-menu'}
                                                        aria-label={t('accessibility.navigation.userMenu')}
                                                        className="text-menu-item-text bg-menu-item-bg hover:bg-menu-item-bg-hover hover:text-menu-item-text-hover  flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                                                        onClick={() => {
                                                            if (isMobileMenuOpen) {
                                                                closeMobileMenu();
                                                            }
                                                        }}
                                                    >
                                                        <Icon className="h-6 w-6 m-1 rounded-full" aria-hidden="true" path={Icon.Path.mdiAccount} />
                                                    </Menu.Button>
                                                </div>
                                                <Transition
                                                    show={isUserMenuOpen}
                                                    as={Fragment}
                                                    enter="transition ease-out duration-100"
                                                    enterFrom="transform opacity-0 scale-95"
                                                    enterTo="transform opacity-100 scale-100"
                                                    leave="transition ease-in duration-75"
                                                    leaveFrom="transform opacity-100 scale-100"
                                                    leaveTo="transform opacity-0 scale-95"
                                                >
                                                    <Menu.Items
                                                        id={'user-menu'}
                                                        static
                                                        className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                                                    >
                                                        <Menu.Item>
                                                            {({ active }) => (
                                                                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                                                <a
                                                                    href="#"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();

                                                                        closeUserMenu();

                                                                        dispatch(push('/management'));
                                                                    }}
                                                                    className={classNames(active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700')}
                                                                >
                                                                    {t('menu.accounts')}
                                                                </a>
                                                            )}
                                                        </Menu.Item>
                                                        <Menu.Item>
                                                            {({ active }) => (
                                                                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                                                <a
                                                                    href="#"
                                                                    onClick={async (e) => {
                                                                        e.preventDefault();

                                                                        closeUserMenu();

                                                                        if (await confirmIfBlocked({ allowNextNavigation: true })) {
                                                                            dispatch(loginActions.logout());
                                                                        }
                                                                    }}
                                                                    className={classNames(active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700')}
                                                                >
                                                                    {t('menu.logout')}
                                                                </a>
                                                            )}
                                                        </Menu.Item>
                                                    </Menu.Items>
                                                </Transition>
                                            </>
                                        )}
                                    </Menu>
                                )}
                                {!isLoggedIn && !isCompactLayout && (
                                    <div className={'icon-buttons flex space-x-4'} role={'menu'} aria-label={t('accessibility.navigation.moreMenu')}>
                                        <HighContrastButton />
                                        <LanguageButton />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <Transition
                        show={isMobileMenuOpen}
                        as={Fragment}
                        unmount={false}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <Disclosure.Panel
                            static={true}
                            className={'md:hidden bg-menu-bg shadow-xl'}
                            as={'nav'}
                            id={'mobile-menu'}
                            role={'menu'}
                            aria-label={t('accessibility.navigation.mobileMenu')}
                        >
                            <div className={'px-2 pt-2 pb-3 space-y-2 mobile-menu-items'} role={'none'}>
                                {navigation.map((item) => (
                                    <Disclosure.Button
                                        as={'a'}
                                        key={item.name}
                                        role={'menuitem'}
                                        href={item.href}
                                        aria-current={item.current ? 'page' : undefined}
                                        className={getMenuClasses(item.current, true)}
                                        onClick={(e: any) => {
                                            e.preventDefault();

                                            closeMobileMenu();

                                            dispatch(push(item.href));
                                        }}
                                    >
                                        {item.name}
                                        {item.current && <span className="sr-only"> ({t('accessibility.navigation.current')})</span>}
                                    </Disclosure.Button>
                                ))}
                                {isCompactLayout && (
                                    <>
                                        <div className={'w-full h-0.5 bg-gray-200 rounded'} />

                                        <HighContrastButton
                                            menuButton
                                            onClick={() => {
                                                closeMobileMenu();
                                            }}
                                        />
                                        <LanguageButton
                                            menuButton
                                            onClick={() => {
                                                closeMobileMenu();
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                        </Disclosure.Panel>
                    </Transition>
                </>
            )}
        </Disclosure>
    );
};

NavigationBar.SearchBar = () => {
    return (
        <div className={'navigation-bar--search'}>
            <input placeholder={'Suche'} />
        </div>
    );
};

NavigationBar.Title = () => {
    return (
        <div className={'navigation-bar--title items-center flex'}>
            <span>Hausverwaltung Dunkel</span>
        </div>
    );
};

NavigationBar.Icon = ({ className }: { className?: string }) => {
    return (
        <div className={classNames('navigation-bar--icon', className)}>
            <img alt="logo" className={'h-8 w-auto'} src={'https://envago.de/img/logo_bw.png'} />
        </div>
    );
};

export default NavigationBar;
